/**
 * Web gather entry point (S4 T3, board R17/R19/N7): one button that reads the client's own site
 * and Google Business Profile into REVIEWABLE DRAFTS. Nothing reaches the live Brand Bible until
 * a person confirms a draft. Renders nothing unless the viewer can edit and the server says this
 * tenant is allowed (the backend flag + allowlist are the single switch, D26; any eligibility
 * error, including a 404 with the backend flag off, hides it); failure wording is plain ("We couldn't read <site>"), never
 * a vendor or a raw code.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { bbSourcesKeys } from '@/hooks/bbSourcesKeys';
import {
  apiErrorCode,
  apiRetryAfterSeconds,
  brandBibleV2Service,
} from '@/services/brandBibleV2Service';
import { DraftRunSummary } from './DraftRunSummary';
import { SummaryBoundary } from './SummaryBoundary';
import { useBrandBibleWizardStore } from '@/stores/brandBibleWizardStore';
import type { GatherEligibility, GatherResponse } from '@/features/brand-bible-wizard/types';

const BLOCKED_OUTCOMES = new Set(['blocked', 'empty', 'timeout', 'error', 'refused']);

const REASON_TEXT: Record<string, string> = {
  filled_v1:
    'This Brand Bible already has content. Web drafting is only available for empty or already-migrated Brand Bibles.',
  no_website: 'Add a website to this client first.',
};

const minutes = (seconds: number) => Math.max(1, Math.ceil(seconds / 60));

export function GatherBanner({
  tenantId,
  siteLabel,
  canEdit,
}: {
  tenantId: string;
  siteLabel: string;
  canEdit: boolean;
}) {
  const enabled = canEdit && !!tenantId;
  const gatherRunning = useBrandBibleWizardStore((s) => s.gatherRunning);
  const queryClient = useQueryClient();
  // The tenant this banner shows NOW: a continuation of a click made for another tenant must not touch it.
  const tenantRef = useRef(tenantId);
  tenantRef.current = tenantId;
  const [eligibility, setEligibility] = useState<GatherEligibility | null>(null);
  const [reasonOverride, setReasonOverride] = useState<string | null>(null);
  const [cooldownOverride, setCooldownOverride] = useState<number | null>(null);
  const [result, setResult] = useState<GatherResponse | null>(null);
  const [inProgress, setInProgress] = useState(false);
  const [failed, setFailed] = useState(false);

  const askEligibility = useCallback(async () => {
    try {
      const e = await brandBibleV2Service.gatherEligibility(tenantId);
      if (tenantRef.current !== tenantId) return;
      setEligibility(e);
      setCooldownOverride(null);
      setReasonOverride(null);
    } catch {
      // Before any answer there is nothing to show (hidden). After one, keep what we have: a
      // failing post-run re-check must not make the finished run's summary vanish (D27). The
      // server still enforces cooldown and eligibility on the next click.
    }
  }, [tenantId]);

  useEffect(() => {
    setEligibility(null);
    setResult(null);
    setFailed(false);
    setInProgress(false);
    if (enabled) void askEligibility();
  }, [enabled, askEligibility]);

  if (!enabled || !eligibility || !eligibility.tenant_allowed) return null;

  const run = async () => {
    const startedFor = tenantId;
    setResult(null);
    setFailed(false);
    setInProgress(false);
    try {
      await useBrandBibleWizardStore.getState().runGather(startedFor);
      if (tenantRef.current !== startedFor) return;
      setResult(useBrandBibleWizardStore.getState().lastGather);
      await askEligibility();
    } catch (err) {
      if (tenantRef.current !== startedFor) return;
      const code = apiErrorCode(err);
      if (code === 'gather_in_progress') {
        setInProgress(true);
      } else if (code === 'gather_cooldown') {
        setCooldownOverride(apiRetryAfterSeconds(err) ?? 300);
      } else if (code === 'not_eligible') {
        const reason = (err as { response?: { data?: { detail?: { reason?: string } } } }).response?.data
          ?.detail?.reason;
        setReasonOverride(reason ?? 'filled_v1');
      } else {
        setFailed(true);
      }
    } finally {
      // The run (even a failed one) may have written drafts and their sources.
      void queryClient.invalidateQueries({ queryKey: bbSourcesKeys.list(tenantId) });
    }
  };

  const reason = reasonOverride ?? eligibility.reason;
  const cooldown = cooldownOverride ?? eligibility.cooldown_s;
  const disabled = gatherRunning || !!reason || cooldown > 0 || !eligibility.eligible;

  const site = (result?.sources ?? []).find((s) => s.kind === 'website');
  const gbp = (result?.sources ?? []).find((s) => s.kind === 'gbp');
  const siteBlocked = !!site && BLOCKED_OUTCOMES.has(site.outcome);
  const partial = !!site && !siteBlocked && site.reason === 'extraction_timeout';
  const created = result?.totals?.created ?? 0;

  return (
    <div data-testid="gather-banner" className="flex flex-col gap-2 rounded-[10px] border border-border px-4 py-3">
      <div className="flex flex-wrap items-center gap-3">
        <div className="min-w-[220px] flex-1">
          <div className="text-[13px] font-semibold">Draft this Brand Bible from {siteLabel}</div>
          <div className="text-xs text-muted-foreground">
            We read the website and Google Business Profile into drafts. Nothing goes live until you confirm it.
          </div>
        </div>
        <button
          type="button"
          data-testid="gather-run"
          disabled={disabled}
          onClick={() => void run()}
          className="rounded-[7px] bg-coral px-3.5 py-1.5 text-[12px] font-semibold text-white hover:bg-coral-hover disabled:opacity-60"
        >
          Draft from website
        </button>
      </div>

      {gatherRunning && (
        <div data-testid="gather-running" className="text-[12px] text-muted-foreground">
          Reading {siteLabel}… this can take a few minutes. You can keep this page open.
        </div>
      )}
      {reason && REASON_TEXT[reason] && (
        <div data-testid="gather-disabled-reason" className="text-[12px] text-muted-foreground">
          {REASON_TEXT[reason]}
        </div>
      )}
      {!reason && cooldown > 0 && (
        <div data-testid="gather-cooldown" className="text-[12px] text-muted-foreground">
          Try again in {minutes(cooldown)} min
        </div>
      )}
      {inProgress && (
        <div data-testid="gather-in-progress" className="text-[12px] text-muted-foreground">
          A draft run for this client is already in progress. Check back in a few minutes.
        </div>
      )}
      {failed && (
        <div data-testid="gather-error" className="text-[12px] text-destructive">
          Something went wrong while drafting. Nothing was published; you can try again.
        </div>
      )}

      {result && (
        <SummaryBoundary resetKey={result}>
          <DraftRunSummary
            result={result}
            source={{ kind: 'site', label: siteLabel }}
            testIdPrefix="gather"
            showHeadline={created > 0 || !siteBlocked}
          />
        </SummaryBoundary>
      )}
      {result && partial && (
        <div data-testid="gather-partial" className="text-[12px] text-muted-foreground">
          Reading {siteLabel} took too long, so some pages may be missing. Run it again later for the rest.
        </div>
      )}
      {result && siteBlocked && (
        <div data-testid="gather-blocked" className="text-[12px] text-muted-foreground">
          We couldn't read {siteLabel}.
        </div>
      )}
      {result && (gbp?.created ?? 0) > 0 && (
        <div data-testid="gather-gbp-note" className="text-[12px] text-muted-foreground">
          Some drafts came from the Google Business Profile.
        </div>
      )}
    </div>
  );
}
